/* Copyright (c) 2017 Backpack Health LLC */
/* global window */

import away from 'away';

import AppUtils from './AppUtils';
import api from 'api';
import AccessTokenModel from 'models/AccessTokenModel';

import { lastActiveTime, logoutEvent, StorageKeys } from './storagePolyfill';

let refreshTimeout;
let checkAfterIdle;
let idleAway;
let onActivityUpdate;

function logout() {
  AppUtils.logout({}, () => {
    const loginUrl = AppUtils.getGroupLoginUrl(AppUtils.getGroupSubdomain());
    window.location.replace(loginUrl);
  })
}

function getMillisecondsToCallRefresh(session) {
  return session.getMillisecsUntilRefresh() * 0.7;
}

function clearRefreshTokenTimer() {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
}

function startRefreshTokenTimer(refreshIntervalTime) {
  clearRefreshTokenTimer();
  refreshTimeout = setTimeout(refreshToken, refreshIntervalTime);
}

function refreshToken() {
  api.sessionApi.refresh().then(sessionData => {
    const accessToken = new AccessTokenModel(sessionData);
    const millisecondsUntilExpire = getMillisecondsToCallRefresh(accessToken);

    if (millisecondsUntilExpire <= 0) {
      logout();
    } else {
      startRefreshTokenTimer(millisecondsUntilExpire);
    }
  }).catch(() => {
    logout();
  });
}

function getNowTime() {
  return new Date().getTime();
}

function checkIfStillIdle(durationMilliseconds) {
  const lastActive = lastActiveTime.get() || 0;
  const nowTime = getNowTime();
  return nowTime - lastActive >= durationMilliseconds;
}

function startIdleTimer(durationMilliseconds) {
  if (checkIfStillIdle(durationMilliseconds)) {
    logout();
    return;
  }
  lastActiveTime.set(getNowTime());

  onActivityUpdate = () => {
    if (checkAfterIdle) {
      window.clearInterval(checkAfterIdle);
      checkAfterIdle = null;
    }

    lastActiveTime.set(getNowTime());
  }

  idleAway = away(durationMilliseconds);
  idleAway.on('idle', () => {
    if (checkIfStillIdle(durationMilliseconds)) {
      logout();
    } else {
      checkAfterIdle = window.setInterval(() => {
        if (checkIfStillIdle(durationMilliseconds)) {
          window.clearInterval(checkAfterIdle);
          checkAfterIdle = null;
          logout();
        }
      }, 1000);
    }
  });
  window.addEventListener('mousemove', onActivityUpdate);
  window.addEventListener('focus', onActivityUpdate);
  window.addEventListener('keypress', onActivityUpdate);

  window.LOGOUT_STORAGE_EVENT_HANDLER = (event) => {
    if (event.key === StorageKeys.LOGOUT_EVENT) {
      logout();
    }
  };
  window.addEventListener('storage', window.LOGOUT_STORAGE_EVENT_HANDLER);

  window.CANCEl_IDLE_TIMERS = stop;
}

function start(accessData) {
  const accessToken = new AccessTokenModel(accessData);
  const millisecondsUntilExpire = getMillisecondsToCallRefresh(accessToken);
  const maxPossibleInterval = Math.pow(2, 31) - 1;
  const refreshIntervalTime = Math.min(millisecondsUntilExpire, maxPossibleInterval);
  startRefreshTokenTimer(refreshIntervalTime);
  lastActiveTime.set(getNowTime());

  startIdleTimer(accessToken.getMillisecsDuration());
}

function stop() {
  clearRefreshTokenTimer();

  if (checkAfterIdle) {
    window.clearInterval(checkAfterIdle);
    checkAfterIdle = null;
  }
  if (idleAway) {
    idleAway.stop();
    idleAway = null;
  }

  if (window.LOGOUT_STORAGE_EVENT_HANDLER) {
    window.removeEventListener('storage', window.LOGOUT_STORAGE_EVENT_HANDLER);
    window.LOGOUT_STORAGE_EVENT_HANDLER = null;
  }

  window.removeEventListener('mousemove', onActivityUpdate);
  window.removeEventListener('focus', onActivityUpdate);
  window.removeEventListener('keypress', onActivityUpdate);
  onActivityUpdate = null;

  window.CANCEl_IDLE_TIMERS = null
}

const ManageSession = { start, stop };

export default ManageSession;
